import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "stepOne", "stepTwo",
    "invoiceCheckbox", "selectedCount",
    "customAmountField", "modeField",
    "paymentTotal", "reviewBtn",
    "reviewTotal", "reviewSubtotal", "reviewFee",
    "reviewInvoiceList", "invoiceSummary",
    "partialWarning", "confirmBtn",
    "paymentMethodRadio",
    "feeBreakdown", "disclosedFeeField"
  ]

  connect() {
    this.updateTotal()
  }

  switchTab() {
    setTimeout(() => this.updateTotal(), 0)
  }

  toggleAll(e) {
    e.preventDefault()
    const allChecked = this.invoiceCheckboxTargets.every(cb => cb.checked)
    this.invoiceCheckboxTargets.forEach(cb => cb.checked = !allChecked)
    this.updateTotal()
  }

  toggleGroup(e) {
    e.preventDefault()
    const group = e.currentTarget.dataset.group
    const groupCheckboxes = this.invoiceCheckboxTargets.filter(cb => cb.dataset.group === group)
    const allChecked = groupCheckboxes.every(cb => cb.checked)
    groupCheckboxes.forEach(cb => cb.checked = !allChecked)
    this.updateTotal()
  }

  updateTotal() {
    const amount = this._computeAmount()
    const formatted = amount > 0 ? this._formatCurrency(amount) : "$0.00"

    this.paymentTotalTarget.textContent = formatted
    this.reviewBtnTarget.disabled = !this._readyToPay()

    const count = this.invoiceCheckboxTargets.filter(cb => cb.checked).length
    this.selectedCountTarget.textContent = count
  }

  _computeAmount() {
    const activeTab = this.element.querySelector(".nav-link.active")?.getAttribute("href")

    if (activeTab === "#tab-total") {
      return parseFloat(this.element.dataset.totalDue || 0)
    }

    if (activeTab === "#tab-custom") {
      return parseFloat(this.customAmountFieldTarget.value || 0)
    }

    return this.invoiceCheckboxTargets
      .filter(cb => cb.checked)
      .reduce((sum, cb) => sum + parseFloat(cb.dataset.amount || 0), 0)
  }

  _selectedPaymentType() {
    return this.paymentMethodRadioTargets.find(r => r.checked)?.dataset.paymentType || null
  }

  _updateReviewFee(amount) {
    const isCard = this._selectedPaymentType() === "card"
    const feePct = parseFloat(this.element.dataset.cardFeePct || 0)
    this.feeBreakdownTarget.classList.toggle("d-none", !isCard)

    if (isCard) {
      const fee = Math.round(100 * amount * feePct) / 100
      const total = amount + fee
      this.reviewSubtotalTarget.textContent = this._formatCurrency(amount)
      this.reviewFeeTarget.textContent      = this._formatCurrency(fee)
      this.reviewTotalTarget.textContent    = this._formatCurrency(total)
      this.disclosedFeeFieldTarget.value    = fee.toFixed(2)
    } else {
      this.reviewTotalTarget.textContent    = this._formatCurrency(amount)
      this.disclosedFeeFieldTarget.value    = ""
    }
  }

  _readyToPay() {
    return this._computeAmount() > 0 && this.paymentMethodRadioTargets.some(r => r.checked)
  }

  _formatCurrency(n) {
    return n.toLocaleString("en-US", { style: "currency", currency: "USD" })
  }

  goToReview(e) {
    e.preventDefault()
    if (!this._readyToPay()) return

    const amount = this._computeAmount()
    this._activeTabHref = this.element.querySelector(".nav-link.active")?.getAttribute("href")

    this._updateReviewFee(amount)

    // Build invoice list if in invoice mode
    const activeTab = this.element.querySelector(".nav-link.active")?.getAttribute("href")
    const isInvoiceMode = activeTab === "#tab-invoices"
    const isCustomMode = activeTab === "#tab-custom"

    this.invoiceSummaryTarget.classList.toggle("d-none", !isInvoiceMode)
    this.partialWarningTarget.classList.toggle("d-none", !isCustomMode)

    if (isInvoiceMode) {
      const list = this.reviewInvoiceListTarget
      list.innerHTML = ""
      this.invoiceCheckboxTargets.filter(cb => cb.checked).forEach(cb => {
        const label = cb.closest("label")
        const number = label.querySelector(".payable-title")?.textContent.trim()
        const amount = this._formatCurrency(parseFloat(cb.dataset.amount))
        list.insertAdjacentHTML("beforeend", `
          <div class="list-group-item d-flex justify-content-between py-2">
            <span class="font-weight-bold small">${number}</span>
            <span class='text-monospace'>${amount}</span>
          </div>
        `)
      })
    }

    // Ensure pay_mode hidden field matches the active tab.
    // The field lives inside #tab-total and is always submitted, so we must
    // clear it when the user is not in "total" mode to prevent apply_total_pay_mode
    // from running instead of the correct handler.
    const modeMap = { "#tab-total": "total", "#tab-invoices": "invoices", "#tab-custom": "custom" }
    this.modeFieldTarget.value = modeMap[activeTab] || ""

    this.stepOneTarget.classList.add("d-none")
    this.stepTwoTarget.classList.remove("d-none")
  }

  goBack() {
    this.stepTwoTarget.classList.add("d-none")
    this.stepOneTarget.classList.remove("d-none")

    if (this._activeTabHref) {
      const tab = this.element.querySelector(`.nav-link[href="${this._activeTabHref}"]`)
      if (tab) $(tab).tab("show")
    }
  }
}